import { icuKey, parseAnswerArg } from '@tabel/shared/answers';
import type { FormKeyView } from '@tabel/shared/form-fields';
import type { RuleField, RuleGroupView } from '@tabel/shared/schemas/rules';
import { Fragment, type ReactNode, useEffect, useMemo } from 'react';
import { reportClientError } from '../../lib/errors';
import {
  ChoiceCheck,
  ChoiceRadios,
  HintToggle,
  InlineInput,
  InlineSelect,
  type RadioRow,
  optionText,
} from './EditFields';
import {
  type AnswerNode,
  type AnswerPart,
  type FieldIndex,
  type Values,
  answerParts,
  answerTree,
  argValues,
  detailLines,
  flagText,
  hasSentence,
  pluralBranch,
  sameValue,
  valueText,
} from './answer';
import { isOffValue } from './catalog';
import { editable, keyOf, typed, visibleField } from './edit';
import { type TermRef, withTerms } from './search';
import { LABEL, T } from './texts';

/** Ответ «Не применять» (FR-056): название ответа, за ним — что тогда делает система. */
function NotApply({ children }: { children: ReactNode }) {
  return (
    <>
      <span className="font-medium">{`${LABEL.notApply}.`}</span> {children}
    </>
  );
}

/** Подпись варианта списка: у значения `offWhen` — «Не применять». */
const notApplyLabel = (g: RuleGroupView, key: string) => (v: unknown) =>
  isOffValue(g, key, v) ? LABEL.notApply : undefined;

/**
 * Ответ правила в просмотре (004, contracts/ui.md, «Карточка правила»): шаблон ICU с сервера, значения жирным;
 * термин в тексте — ссылка на определение (FR-008), только первое место в ответе. Без `terms` — без ссылок
 * (документ для печати). `values` — значения черновика (ответ по черновику, FR-026). Шаблон не отрисовался —
 * понятный текст, ошибка уходит в серверный журнал (принцип II).
 */
export function AnswerText({
  group,
  fields,
  terms = [],
  values,
}: {
  group: RuleGroupView;
  fields: FieldIndex;
  terms?: readonly TermRef[];
  values?: Values;
}) {
  const result = useMemo((): { parts: AnswerPart[] } | { error: unknown } => {
    try {
      return { parts: answerParts(group, fields, values) };
    } catch (error) {
      return { error };
    }
  }, [group, fields, values]);
  const failed = 'error' in result ? result.error : null;
  useEffect(() => {
    if (failed) reportClientError('Rules', `render answer ${group.id}`, failed);
  }, [failed, group.id]);

  if ('error' in result) return <p className="text-[13px] text-ink-3">{T.answerError}</p>;
  const used = new Set<string>();
  return (
    <p data-answer className="text-[14.5px] leading-[1.6] text-pretty break-words text-ink">
      {result.parts.map((p) =>
        p.value ? (
          <b key={p.at} className="font-semibold">
            {p.text}
          </b>
        ) : (
          <Fragment key={p.at}>
            {withTerms(p.text, terms, used, p.at).map((x) =>
              x.term ? (
                <a
                  key={x.at}
                  href={`#${x.term.id}`}
                  title={x.term.text}
                  className="underline decoration-ink-3 decoration-dotted underline-offset-[3px] hover:text-accent"
                >
                  {x.text}
                </a>
              ) : (
                <Fragment key={x.at}>{x.text}</Fragment>
              ),
            )}
          </Fragment>
        ),
      )}
    </p>
  );
}

/**
 * Поля под ответом: «подпись: значение», значение жирным; пустые не показываются. В карточке правила — свёрнуты под
 * «Подробнее о правиле» (`collapsed`): ответ и пример читаются без них (отзыв пользователя 16.10.2026: «очень сложно»).
 * В печати — развёрнуты.
 */
export function AnswerDetails({
  group,
  fields,
  values,
  collapsed = false,
}: {
  group: RuleGroupView;
  fields: FieldIndex;
  values?: Values;
  collapsed?: boolean;
}) {
  const lines = detailLines(group, fields, values);
  if (lines.length === 0) return null;
  const list = (
    <div className="space-y-0.5 text-[13px] text-ink-2">
      {lines.map((d) => (
        <p key={d.key} className="break-words">
          {d.label}: <b className="font-semibold text-ink">{d.value}</b>
        </p>
      ))}
    </div>
  );
  if (!collapsed) return list;
  return (
    <details data-answer-details className="text-[13px]">
      <summary className="w-fit cursor-pointer select-none text-ink-3 hover:text-ink-2">
        {LABEL.ruleDetails}
      </summary>
      <div className="mt-1">{list}</div>
    </details>
  );
}

// ── Изменение на месте (T063): значения — поля внутри предложений ──

/** Что нужно ответу в изменении: введённые значения и как их менять. */
export interface EditModel {
  readonly group: RuleGroupView;
  readonly fields: FieldIndex;
  readonly values: Values;
  /** Значения действующей версии: изменённое поле выделено, прежнее значение — зачёркнутым. */
  readonly base: Values;
  /** Введённый текст полей-чисел и времени. */
  readonly raw: Readonly<Record<string, string>>;
  /** Единица ввода поля (`inputScale`): 60 — поле в секундах вводится в минутах; нет — 1. */
  readonly scale?: (key: string) => number;
  readonly invalid: (key: string) => boolean;
  /** Текст ошибки или предупреждения у поля: `id` для `aria-describedby`. */
  readonly describedBy: (key: string) => string | undefined;
  readonly set: (key: string, value: unknown) => void;
  readonly type: (key: string, raw: string) => void;
}

interface Walk {
  readonly m: EditModel;
  /** Значения этой ветки: у невыбранного варианта — с его значением. */
  readonly values: Values;
  readonly argv: Record<string, string | number>;
  /** Поля в ветке меняются (выбранная ветка); иначе — текст серым. */
  readonly live: boolean;
  /** Поля, которые уже стоят в ответе: второе место того же поля — текстом. */
  readonly placed: Set<string>;
  readonly pound?: number;
}

/** Текст значения в изменении; значения нет (у невыбранного варианта поле пустое) — «…». */
const textOf = (m: EditModel, values: Values) => (key: string) =>
  values[key] === undefined || values[key] === null ? '…' : valueText(m.group, key, values, m.fields);

function walkWith(w: Walk, values: Values, live: boolean): Walk {
  return {
    ...w,
    values,
    live,
    argv: argValues(w.m.group, w.m.fields, values, textOf(w.m, values)),
  };
}

/** Текст ветки без полей — подпись варианта для чтения с экрана. */
function plainText(nodes: readonly AnswerNode[] | undefined, w: Walk): string {
  let out = '';
  for (const n of nodes ?? []) {
    if (n.type === 0) out += n.value;
    else if (n.type === 1) {
      const a = parseAnswerArg(w.m.group.args[n.value] ?? '');
      out += a ? textOf(w.m, w.values)(a.key) : '';
    } else if (n.type === 5) {
      const sel = String(w.argv[n.value] ?? 'other');
      out += plainText((n.options[sel] ?? n.options.other)?.value, w);
    } else if (n.type === 6) out += plainText(pluralBranch(n, Number(w.argv[n.value] ?? 0)), w);
    else if (n.type === 7) out += String(w.pound ?? '');
    else if (n.type === 8) out += plainText(n.children, w);
  }
  return out.replace(/\s+/gu, ' ').trim();
}

/** Есть ли в ветке поле, которое меняется (тогда строка варианта — не `label`). */
function branchHasFields(nodes: readonly AnswerNode[] | undefined, m: EditModel): boolean {
  return (nodes ?? []).some((n) => {
    if (n.type === 1 || n.type === 5) {
      const a = parseAnswerArg(m.group.args[n.value] ?? '');
      if (a && editable(m.group, m.fields.get(a.key))) return true;
    }
    if (n.type === 5 || n.type === 6)
      return Object.values(n.options).some((b) => branchHasFields(b.value, m));
    return n.type === 8 ? branchHasFields(n.children, m) : false;
  });
}

function nodes(list: readonly AnswerNode[] | undefined, w: Walk): ReactNode[] {
  return (list ?? []).map((n, i) => <Fragment key={`${i}-${n.type}`}>{node(n, w)}</Fragment>);
}

function valueNode(key: string, w: Walk): ReactNode {
  const text = textOf(w.m, w.values)(key);
  return <b className="font-semibold">{text}</b>;
}

function node(n: AnswerNode, w: Walk): ReactNode {
  const { m } = w;
  switch (n.type) {
    case 0:
      return n.value;
    case 7:
      return String(w.pound ?? '');
    case 8:
      return nodes(n.children, w);
    case 6: {
      const count = Number(w.argv[n.value] ?? 0);
      return nodes(pluralBranch(n, count), { ...w, pound: count - (n.offset ?? 0) });
    }
    case 5:
      return selectNode(n, w);
    default: {
      const a = parseAnswerArg(m.group.args[n.value] ?? '');
      if (!a) return '';
      const f = m.fields.get(a.key);
      if (w.live && f && typed(f) && (editable(m.group, f) || f.locked) && !w.placed.has(a.key)) {
        w.placed.add(a.key);
        return fieldInput(f, w);
      }
      return valueNode(a.key, w);
    }
  }
}

function fieldInput(f: RuleField, w: Walk): ReactNode {
  const { m } = w;
  const key = keyOf(f);
  const changed = !sameValue(m.values[key], m.base[key]);
  return (
    <InlineInput
      field={f}
      raw={m.raw[key] ?? ''}
      scale={m.scale?.(key)}
      invalid={m.invalid(key)}
      changed={changed}
      was={m.group.texts[key]}
      describedBy={m.describedBy(key)}
      onChange={(raw) => m.type(key, raw)}
    />
  );
}

function selectNode(n: Extract<AnswerNode, { type: 5 }>, w: Walk): ReactNode {
  const { m } = w;
  const selector = String(w.argv[n.value] ?? 'other');
  const branch = (n.options[selector] ?? n.options.other)?.value;
  const a = parseAnswerArg(m.group.args[n.value] ?? '');
  const f = a ? m.fields.get(a.key) : undefined;
  if (!w.live || !a || !f || !editable(m.group, f)) return nodes(branch, w);
  const place = a.option === undefined ? a.key : `${a.key}~${a.option}`;
  if (w.placed.has(place)) return nodes(branch, w);
  w.placed.add(place);
  const key = a.key;
  const current = w.values[key];
  const changed = !sameValue(m.values[key], m.base[key]);

  // Значение в списке (`поле~значение`): флажок, текст — что делает система с этим значением.
  if (a.option !== undefined) {
    const list = Array.isArray(current) ? current.map(String) : [];
    const on = list.includes(a.option);
    const content = n.options.true?.value;
    return (
      <ChoiceCheck
        label={`${f.label}: ${optionText(f, a.option)}`}
        checked={on}
        hasFields={branchHasFields(content, m)}
        onChange={(checked) => m.set(key, checked ? [...list, a.option] : list.filter((x) => x !== a.option))}
      >
        {nodes(content, on ? w : walkWith(w, { ...w.values, [key]: [...list, a.option] }, false))}
      </ChoiceCheck>
    );
  }

  // Да/нет у правила, которое можно не применять (FR-056): два ответа — правило и «Не применять».
  if (f.control === 'boolean' && m.group.offWhen?.field === key) {
    const branchOf = (v: boolean) => (n.options[String(v)] ?? n.options.other)?.value;
    const rows: RadioRow[] = [true, false].map((v) => {
      const b = branchOf(v);
      const ow = sameValue(v, current) ? w : walkWith(w, { ...w.values, [key]: v }, false);
      const off = isOffValue(m.group, key, v);
      return {
        value: v,
        label: off ? LABEL.notApply : plainText(b, ow) || f.label,
        hasFields: branchHasFields(b, m),
        content: off ? <NotApply>{nodes(b, ow)}</NotApply> : nodes(b, ow),
      };
    });
    return (
      <ChoiceRadios
        label={f.label}
        value={current}
        rows={rows}
        inline={false}
        onPick={(v) => m.set(key, v)}
      />
    );
  }

  // Да/нет — флажок.
  if (f.control === 'boolean') {
    const on = current === true;
    const yes = n.options.true?.value;
    const content = yes && yes.length > 0 ? yes : n.options.other?.value;
    return (
      <ChoiceCheck
        label={f.label}
        checked={on}
        hasFields={branchHasFields(content, m)}
        onChange={(checked) => m.set(key, checked)}
      >
        {/* Текст флажка начинается с блока вариантов — сначала название флажка (как у строки варианта). */}
        {on && startsWithBlock(content, m) && <span className="block text-ink">{f.label}</span>}
        {nodes(content, on ? w : walkWith(w, { ...w.values, [key]: true }, false))}
      </ChoiceCheck>
    );
  }

  const options = f.options ?? [];
  // Вариант из 5 и больше — список; за ним — предложение выбранного варианта.
  if (f.control === 'select' && options.length > 4) {
    return (
      <>
        <InlineSelect
          field={f}
          value={current}
          changed={changed}
          onChange={(v) => m.set(key, v)}
          optionLabel={notApplyLabel(m.group, key)}
        />{' '}
        {nodes(branch, w)}
      </>
    );
  }

  // Вариант из 2–4 — радио: у каждого варианта — его предложение.
  if (f.control === 'select' && options.length >= 2) {
    const branchOf = (v: unknown) => n.options[icuKey(v)] ?? n.options.other;
    const inline = !options.some((o) => hasSentence(branchOf(o.value)?.value));
    const variants = options.map((o) => {
      const b = branchOf(o.value);
      const on = sameValue(o.value, current);
      const ow = on ? w : walkWith(w, { ...w.values, [key]: o.value }, false);
      return { o, b, ow, text: plainText(b?.value, ow) };
    });
    const rows: RadioRow[] = variants.map(({ o, b, ow, text }) => {
      // «Не применять» (FR-056): значение `offWhen` — свой ответ, за ним — что тогда делает система.
      if (isOffValue(m.group, key, o.value))
        return {
          value: o.value,
          label: LABEL.notApply,
          hasFields: branchHasFields(b?.value, m),
          content: <NotApply>{nodes(b?.value, ow)}</NotApply>,
        };
      const doc = o.doc ?? String(o.value);
      // Два варианта с одним предложением (ветка `other`) — у каждого ещё и его название.
      const twin = variants.some((x) => x.o !== o && x.text === text);
      const lead = sameValue(o.value, current) && startsWithBlock(b?.value, m);
      return {
        value: o.value,
        label: o.doc ?? (text || doc),
        hasFields: branchHasFields(b?.value, m),
        content: (
          <>
            {lead && <span className="block text-ink">{doc}</span>}
            {!lead && (twin || !text) && (
              <span className="font-medium">{`${doc.replace(/[.!]$/u, '')}${text ? ': ' : ''}`}</span>
            )}
            {nodes(b?.value, ow)}
          </>
        ),
      };
    });
    return (
      <ChoiceRadios
        label={f.label}
        value={current}
        rows={rows}
        inline={inline}
        caption={severalBlocks(m.group, m.fields)}
        hint={f.hint}
        onPick={(v) => m.set(key, v)}
      />
    );
  }
  return nodes(branch, w);
}

/** Ответ в изменении: тот же шаблон, значения — поля внутри предложений. */
export function AnswerEdit({ model }: { model: EditModel }) {
  const tree = useMemo(() => {
    try {
      return { nodes: answerTree(model.group.answer) };
    } catch (error) {
      return { error };
    }
  }, [model.group.answer]);
  const failed = 'error' in tree ? tree.error : null;
  useEffect(() => {
    if (failed) reportClientError('Rules', `render answer edit ${model.group.id}`, failed);
  }, [failed, model.group.id]);
  if ('error' in tree) return <p className="text-[13px] text-ink-3">{T.answerError}</p>;
  const w: Walk = {
    m: model,
    values: model.values,
    live: true,
    placed: new Set(),
    argv: argValues(model.group, model.fields, model.values, textOf(model, model.values)),
  };
  return (
    <div data-answer-edit className="text-[14.5px] leading-[2] text-pretty break-words text-ink">
      {nodes(tree.nodes, w)}
    </div>
  );
}

/** Ключи полей ответа: аргументы шаблона. */
/**
 * Ветка начинается с блока вариантов (поле-выбор из 2–4 вариантов): у выбранной строки сначала — название варианта,
 * иначе рядом с кружком стоит подпись вложенного блока, будто это название варианта (обед, приёмка 16.10.2026).
 */
function startsWithBlock(list: readonly AnswerNode[] | undefined, m: EditModel): boolean {
  const first = list?.find((x) => !(x.type === 0 && x.value.trim() === ''));
  if (first?.type !== 5) return false;
  const a = parseAnswerArg(m.group.args[first.value] ?? '');
  const f = a && a.option === undefined ? m.fields.get(a.key) : undefined;
  const n = f?.options?.length ?? 0;
  return f !== undefined && f.control === 'select' && n >= 2 && n <= 4 && editable(m.group, f);
}

/**
 * В ответе несколько блоков вариантов (поле-выбор из 2–4 вариантов в предложениях): тогда у каждого блока — подпись.
 */
function severalBlocks(g: RuleGroupView, fields: FieldIndex): boolean {
  const inAnswer = argKeys(g);
  let n = 0;
  for (const f of g.fields) {
    const k = keyOf(f);
    const own = fields.get(k) ?? f;
    const count = own.options?.length ?? 0;
    if (inAnswer.has(k) && own.control === 'select' && count >= 2 && count <= 4 && !own.unused) n++;
  }
  return n >= 2;
}

function argKeys(g: RuleGroupView): Set<string> {
  const out = new Set<string>();
  for (const spec of Object.values(g.args)) {
    const a = parseAnswerArg(spec);
    if (a && !a.count) out.add(a.key);
  }
  return out;
}

/** Поле под ответом в изменении: «подпись: поле» (data-model А.2, `details`). */
function DetailField({ f, m }: { f: RuleField; m: EditModel }) {
  const key = keyOf(f);
  const v = m.values[key];
  if (f.unused || f.teamOnly) {
    return (
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-ink-2">{f.label}:</span>
        <HintToggle label={f.label} hint={f.hint} />
        <b className={`font-semibold ${f.unused ? 'text-ink-3' : 'text-ink'}`}>
          {valueText(m.group, key, m.values, m.fields) || '—'}
        </b>
        <span className="text-[12.5px] text-ink-3">{f.unused ? T.notInCalc : T.teamValue}</span>
      </div>
    );
  }
  const changed = !sameValue(v, m.base[key]);
  const options = f.options ?? [];
  const can = editable(m.group, f);
  const empty = v === undefined || v === null;
  // Пустое значение, которое здесь не вводится (как в просмотре), не показывается.
  const choosable =
    (f.control === 'select' && options.length >= 2) || f.control === 'boolean' || f.control === 'multiselect';
  if (empty && !(can && (typed(f) || choosable))) return null;
  let control: ReactNode;
  if (typed(f) && (can || f.locked)) {
    control = (
      <InlineInput
        field={f}
        raw={m.raw[key] ?? ''}
        scale={m.scale?.(key)}
        invalid={m.invalid(key)}
        changed={changed}
        was={m.group.texts[key]}
        describedBy={m.describedBy(key)}
        onChange={(raw) => m.type(key, raw)}
      />
    );
  } else if (!can || (f.control === 'select' && options.length < 2)) {
    // Выбирать нечего (закон, один вариант) — значение текстом.
    control = <b className="font-semibold text-ink">{valueText(m.group, key, m.values, m.fields) || '—'}</b>;
  } else if (f.control === 'boolean' && m.group.offWhen?.field === key) {
    // Правило можно не применять (FR-056): «да» и «Не применять».
    const own = notApplyLabel(m.group, key);
    control = (
      <ChoiceRadios
        label={f.label}
        value={v}
        inline
        onPick={(x) => m.set(key, x)}
        rows={[true, false].map((x) => ({
          value: x,
          label: own(x) ?? (x ? 'да' : 'нет'),
          content: own(x) ?? (x ? 'да' : 'нет'),
        }))}
      />
    );
  } else if (f.control === 'boolean') {
    control = (
      <ChoiceCheck label={f.label} checked={v === true} onChange={(c) => m.set(key, c)}>
        {v === true ? 'да' : 'нет'}
      </ChoiceCheck>
    );
  } else if (f.control === 'select' && options.length > 4) {
    control = (
      <InlineSelect
        field={f}
        value={v}
        changed={changed}
        onChange={(x) => m.set(key, x)}
        optionLabel={notApplyLabel(m.group, key)}
      />
    );
  } else if (f.control === 'select' && options.length >= 2) {
    const own = notApplyLabel(m.group, key);
    control = (
      <ChoiceRadios
        label={f.label}
        value={v}
        inline
        onPick={(x) => m.set(key, x)}
        rows={options.map((o) => ({
          value: o.value,
          label: own(o.value) ?? o.doc ?? String(o.value),
          content: own(o.value) ?? o.doc ?? String(o.value),
        }))}
      />
    );
  } else if (f.control === 'multiselect' && options.length > 0) {
    const list = Array.isArray(v) ? v : [];
    control = (
      <span className="flex flex-col">
        {options.map((o) => {
          const on = list.some((x) => sameValue(x, o.value));
          const text = flagText(o.doc ?? String(o.value));
          return (
            <ChoiceCheck
              key={String(o.value)}
              label={`${f.label}: ${text}`}
              checked={on}
              disabled={o.unused === true}
              onChange={(c) =>
                m.set(key, c ? [...list, o.value] : list.filter((x) => !sameValue(x, o.value)))
              }
            >
              {text}
              {o.unused && <span className="text-[12.5px] text-ink-3"> — {T.notInCalc}</span>}
            </ChoiceCheck>
          );
        })}
      </span>
    );
  } else if (f.control === 'map' && typeof v === 'object' && v !== null && !Array.isArray(v)) {
    control = <MapField f={f} m={m} value={v as Record<string, unknown>} />;
  } else {
    control = (
      <>
        <b className="font-semibold text-ink">{valueText(m.group, key, m.values, m.fields)}</b>{' '}
        <span className="text-ink-3">{T.teamValue}</span>
      </>
    );
  }
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="text-ink-2">{f.label}:</span>
      <HintToggle label={f.label} hint={f.hint} />
      {control}
    </div>
  );
}

/** Словарь по пометкам: у каждой пометки — вариант или «да/нет». Пометки — из значения и из формы. */
function MapField({ f, m, value }: { f: RuleField; m: EditModel; value: Record<string, unknown> }) {
  const key = keyOf(f);
  const formKeys = (f as { keys?: readonly FormKeyView[] }).keys ?? [];
  const codes = [
    ...new Set([...formKeys.filter((k) => k.editable).map((k) => k.code), ...Object.keys(value)]),
  ];
  // Название пометки — с сервера (`keys[].label`, `PLAIN_FLAGS` движка); нет — из кода пометки.
  const named = new Map(formKeys.flatMap((k) => (k.label ? [[k.code, k.label] as const] : [])));
  const flagName = (code: string) => named.get(code) ?? flagText(code);
  const options = f.options ?? [];
  const bool = options.length === 0 && Object.values(value).every((x) => typeof x === 'boolean');
  if (!bool && options.length === 0) {
    return <b className="font-semibold text-ink">{valueText(m.group, key, m.values, m.fields)}</b>;
  }
  const put = (code: string, x: unknown) => m.set(key, { ...value, [code]: x });
  return (
    <span className="flex w-full flex-col">
      {codes.map((code) => {
        const label = `${f.label}: ${flagName(code)}`;
        return bool ? (
          <ChoiceCheck key={code} label={label} checked={value[code] === true} onChange={(c) => put(code, c)}>
            {flagName(code)}
          </ChoiceCheck>
        ) : (
          <span key={code} className="flex flex-wrap items-baseline gap-x-2 max-sm:min-h-10">
            <span className="text-ink-2">{flagName(code)}</span>
            <InlineSelect
              field={{ ...f, label }}
              value={value[code]}
              changed={!sameValue(value[code], (m.base[key] as Record<string, unknown> | undefined)?.[code])}
              onChange={(x) => put(code, x)}
            />
          </span>
        );
      })}
    </span>
  );
}

/** Поля под ответом в изменении: поля `details` и поля правила, которых нет в предложениях. */
export function DetailsEdit({ model }: { model: EditModel }) {
  const g = model.group;
  const inAnswer = argKeys(g);
  // Поле, которое расчёт пока не учитывает или меняет команда, — под ответом с пометкой, даже если оно в предложении.
  const keys = [
    ...new Set([
      ...g.details,
      ...g.fields.map(keyOf).filter((k) => {
        const own = model.fields.get(k);
        return !inAnswer.has(k) || own?.unused === true || own?.teamOnly === true;
      }),
    ]),
  ];
  const shown = keys.flatMap((k) => {
    const f = model.fields.get(k);
    return f && g.fields.some((x) => keyOf(x) === k) && visibleField(f, model.values) ? [f] : [];
  });
  if (shown.length === 0) return null;
  return (
    <div data-details-edit className="space-y-1 text-[13.5px] leading-[1.9]">
      {shown.map((f) => (
        <DetailField key={keyOf(f)} f={f} m={model} />
      ))}
    </div>
  );
}
